/**
 * The Raffle Detail container
 */
import { Medium } from "@bkry/bowline-components/Medium";
import { EditorLoader } from "@bkry/bowline-components/PageBuilder";
import { RaffleWinners } from "@bkry/bowline-components/RaffleWinners";
import { GenericErrorModal } from "@bkry/bowline-components/StatusModals";
import { UnlockButton } from "@bkry/bowline-components/UnlockButton";
import { UnlockTokenSelector } from "@bkry/bowline-components/UnlockTokenSelector";
import {
  PrettyType,
  ScrollToTop as useScrollToTop,
} from "@bkry/bowline-components/Utils";
import { getProjectAttributes } from "@bkry/bowline-redux/projects";
import {
  getRaffleAttributes,
  getRaffleRelationships,
  prepareUnlockRaffle,
  showRaffle,
  showRaffleErrorReset,
  unlockRaffle,
} from "@bkry/bowline-redux/raffles";
import { getEnv, localizedDate } from "@bkry/bowline-utils";
import { useSsrEffect } from "@issr/core";
import { useWeb3React } from "@web3-react/core";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { FaRegBookmark, FaRegCalendarAlt } from "react-icons/fa";
import { GrLocation } from "react-icons/gr";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Badge, Col, Container, Row } from "reactstrap";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import Page from "../../components/Page/Page-component";

/**
 * Raffle Detail container used as app default landing page
 */
const RaffleDetail = (props, context) => {
  const REACT_APP_PROJECT_ID = getEnv("REACT_APP_PROJECT_ID");
  useScrollToTop();
  const { account, provider, connector } = useWeb3React();
  const { raffleId } = useParams();
  const dispatch = useDispatch();
  const [tokenSelectorOpen, toggleTokenSelector] = useState(false);
  const lang = useSelector((state) => state.i18nState.lang);

  const raffleData = useSelector((state) =>
    getRaffleAttributes(state, raffleId)
  );

  const dispatchApi = async () => {
    // TODO: Reduce the amount of requests done to the API
    dispatch(showRaffle({ projectId: REACT_APP_PROJECT_ID, id: raffleId }));
  };

  useSsrEffect(dispatchApi);
  useEffect(dispatchApi, [dispatch, raffleId, lang]);

  const unlockError = useSelector((state) => state?.raffles?.unlockError);
  const prepareUnlockError = useSelector(
    (state) => state?.raffles?.prepareUnlockError
  );
  const unlockSuccess = useSelector((state) => state?.raffles?.unlockSuccess);
  const error = useSelector((state) => state?.raffles?.showError);

  let startDate = localizedDate(raffleData?.starts_at);
  let endDate = localizedDate(raffleData?.ends_at);

  const projectData = useSelector((state) =>
    getProjectAttributes(state, REACT_APP_PROJECT_ID)
  );

  const unlockContent = (tokens, unlockInputsValues = undefined) => {
    if (raffleData && projectData) {
      dispatch(
        prepareUnlockRaffle({
          account,
          provider,
          connector,
          title: raffleData.title,
          project_title: projectData.title,
          // unlock_boundary_id: 1, // If only one unlock boundary exists we ignore this, if multiple we check which one matches
          token_identifier: tokens[0].identifier,
          contract_address: tokens[0].contract_address,
          tokens,
          chainId: projectData?.default_network_options?.chain_id,
          callbackSuccess: ({
            signature,
            token_identifier,
            contract_address,
            unlock_boundary_id,
          }) => {
            dispatch(
              unlockRaffle({
                signature,
                token_identifier,
                contract_address,
                id: raffleId,
                project_id: REACT_APP_PROJECT_ID,
                unlock_boundary_id,
                unlock_input_values: unlockInputsValues,
                tokens,
                callbackSuccess: () => {
                  console.log("Successfully unlocked");
                },
              })
            );
          },
        })
      );
    }
  };

  const raffleName = raffleData?.title || context.t("Missing title");

  let unlockInputIdsArray = [];

  const raffleRelationships = useSelector((state) =>
    getRaffleRelationships(state, raffleId)
  );
  raffleRelationships?.unlock_inputs?.data.map((inputId) =>
    unlockInputIdsArray.push(inputId)
  );
  const currentTime = moment().format();

  return (
    <Page id="RaffleDetail" title={raffleName}>
      <Container>
        <Breadcrumbs
          breadcrumbs={[
            {
              target: `/${lang}/community/benefits`,
              label: context.t("Holder Benefits"),
              active: false,
            },
            {
              target: `/${lang}/community/benefits`,
              label: context.t("Raffles"),
              active: false,
            },
            {
              target: `/${lang}/community/benefits/raffles/${raffleId}`,
              label: raffleData?.title || context.t("Raffle"),
              active: true,
            },
          ]}
        />
      </Container>
      <Container>
        <Row>
          <Col md={{ size: 6, order: 2 }} className="mb-3">
            {raffleData?.public_medium_original &&
              raffleData?.public_medium_metadata && (
                <Medium
                  mediumData={{
                    ...raffleData?.public_medium_original,
                    ...raffleData?.public_medium_metadata,
                  }}
                  className="justify-content-center mb-3"
                  objectFit="contain"
                  altTitle={raffleName}
                  skipLazyLoading
                />
              )}
          </Col>
          <Col md={{ size: 6, order: 1 }} className="mb-3">
            <h1>{raffleName}</h1>
            {raffleData?.current_user_registered ? (
              <Badge className="mb-2 text-dark" color="primary">
                {context.t("You are registered for this raffle")}
                {raffleData.current_user_registered > 1 &&
                  ` x ${raffleData.current_user_registered}`}
              </Badge>
            ) : (
              ""
            )}
            {raffleData?.raffle_type && (
              <p className="small">
                <FaRegBookmark /> {context.t("Raffle Type")}:{" "}
                <PrettyType type={raffleData?.raffle_type} />
              </p>
            )}
            {raffleData?.location && (
              <>
                <p className="h4 small">
                  <GrLocation /> {raffleData?.location}
                </p>
              </>
            )}
            {raffleData?.public_preview && (
              <div
                className="mb-3"
                dangerouslySetInnerHTML={{
                  __html: raffleData.public_preview.replace(
                    /href=/g,
                    " target='_blank' rel='noopener noreferrer' href="
                  ),
                }}
              />
            )}
            <Row>
              <Col>
                <p className="mt-4 h4 small">
                  <FaRegCalendarAlt /> {context.t("Start Date")}
                </p>
                <p className="small">{startDate}</p>
              </Col>
              <Col>
                <p className="mt-4 h4 small">
                  <FaRegCalendarAlt /> {context.t("End Date")}
                </p>
                <p className="small">{endDate}</p>
              </Col>
            </Row>
            {!raffleData?.current_user_registered &&
              moment(currentTime).isBefore(endDate) && (
                <UnlockButton
                  confirmedEmailRequired={raffleData?.confirmed_email_required}
                  toggleTokenSelector={toggleTokenSelector}
                  color="primary"
                  className="w-100 my-3"
                >
                  {raffleData?.unlock_cta
                    ? raffleData?.unlock_cta
                    : context.t("Sign up for this raffle")}
                </UnlockButton>
              )}

            {moment(currentTime).isAfter(raffleData?.ends_at) && (
              <div className="d-flex justify-content-center m-3">
                {" "}
                <p>
                  <strong>{context.t("This raffle has ended.")}</strong>
                </p>
              </div>
            )}

            {raffleData?.public_description && (
              <div
                className="mb-3"
                dangerouslySetInnerHTML={{
                  __html: raffleData.public_description.replace(
                    /href=/g,
                    " target='_blank' rel='noopener noreferrer' href="
                  ),
                }}
              />
            )}
          </Col>
        </Row>
        <div className="d-flex flex-column">
          {!!raffleData?.raffled_at &&
            !!raffleRelationships?.winning_raffle_registrations?.data && (
              <div>
                <p className="h4 mt-4">{context.t("Raffle Winners")}</p>
                <RaffleWinners raffleId={raffleId} />
              </div>
            )}
        </div>
        {tokenSelectorOpen && (
          <UnlockTokenSelector
            isOpen={tokenSelectorOpen}
            toggleModal={toggleTokenSelector}
            raffleId={raffleId}
            confirmUnlockFeedbackFunction={unlockContent}
            unlockInputIdsArray={unlockInputIdsArray}
            boundaryCombination={raffleData?.boundary_combination}
            unlockSuccess={unlockSuccess}
            unlockTitle={context.t("Unlocking Content")}
            successTitle={context.t("Successfully Unlocked")}
            successMessage={
              raffleData?.unlocked_message
                ? raffleData.unlocked_message
                : context.t("You have signed up for this raffle")
            }
            successReset={showRaffleErrorReset}
            successCta={context.t("View raffle")}
            unlockError={unlockError || prepareUnlockError}
            errorReset={showRaffleErrorReset}
            errorRaw={
              unlockError?.attributes?.full_error_message || prepareUnlockError
            }
            errorMessage={context.t(
              "There was a problem while signing up for this raffle."
            )}
          />
        )}
        {error && (
          <GenericErrorModal
            error={error}
            errorReset={showRaffleErrorReset}
            rawError={context.t(
              "There was a problem showing this raffle. Please contact support on discord"
            )}
            primaryAction={{
              title: context.t("ok"),
              action: showRaffleErrorReset,
            }}
          />
        )}
        <EditorLoader pageSlug={"raffle"} />
      </Container>
    </Page>
  );
};

/**  define proptype for the 'translation' function  */
RaffleDetail.contextTypes = {
  t: PropTypes.func,
};

/** export the component as redux connected component */
export default RaffleDetail;
